import { Plus, Minus, Trash2, Edit3, Presentation } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import type { PropostaItem } from '@/types'
import { getItemTipoConfig } from './types'
import type { ComboData } from './types'

interface ItemTableProps {
  itens: PropostaItem[]
  combos: ComboData[]
  canEdit?: boolean
  onRemoveItem: (id: string) => void
  onUpdateQty: (id: string, qty: number) => void
  onEditItem?: (item: PropostaItem, index: number) => void
  onOpenStudio?: (item: PropostaItem, index: number) => void
}

export function ItemTable({
  itens,
  combos,
  canEdit = true,
  onRemoveItem,
  onUpdateQty,
  onEditItem,
  onOpenStudio,
}: ItemTableProps) {
  if (itens.length === 0) {
    return (
      <p className="text-xs text-text-label italic py-3">
        Nenhum item adicionado. Use a busca de planos acima (💎) ou adicione itens manualmente.
      </p>
    )
  }

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-[1fr_60px_80px_90px_90px] gap-2 text-[10px] text-text-label uppercase tracking-wider px-1">
        <span>Descrição</span>
        <span className="text-center">Qtd</span>
        <span className="text-right">Unitário</span>
        <span className="text-right">Total</span>
        <span className="text-center">Ações</span>
      </div>

      {itens.map((item, idx) => {
        const tipoCfg = getItemTipoConfig(item.tipo)
        const TipoIcon = tipoCfg.icon
        const itemCombos = combos.filter((c) => c.itemIds.includes(item.id))
        return (
          <div
            key={item.id}
            className="grid grid-cols-[1fr_60px_80px_90px_90px] gap-2 items-center p-2 rounded-md bg-white/[0.03] border border-white/[0.06]"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <TipoIcon size={11} className="text-accent shrink-0" />
                <span className="text-xs text-text-primary truncate">{item.descricao}</span>
                {itemCombos.length > 0 && (
                  <span className="text-[10px] text-emerald-400 shrink-0">
                    ({itemCombos.map((c) => c.nome).join(', ')})
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => onUpdateQty(item.id, item.quantidade - 1)}
                className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-colors"
              >
                <Minus size={10} />
              </button>
              <span className="text-xs text-text-primary w-5 text-center">{item.quantidade}</span>
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => onUpdateQty(item.id, item.quantidade + 1)}
                className="p-0.5 rounded text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-colors"
              >
                <Plus size={10} />
              </button>
            </div>
            <span className="text-xs text-text-label text-right">
              {formatCurrency(item.valorUnitario)}
            </span>
            <span className="text-xs font-medium text-text-primary text-right">
              {formatCurrency(item.total)}
            </span>
            <div className="flex items-center justify-center gap-1">
              {onEditItem && (
                <button
                  onClick={() => onEditItem(item, idx)}
                  className="p-1 rounded text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 transition-colors"
                  title="Editar Escopo/Serviços"
                >
                  <Edit3 size={12} />
                </button>
              )}
              {onOpenStudio && (
                <button
                  onClick={() => onOpenStudio(item, idx)}
                  className="p-1 rounded text-orange-400 hover:text-orange-300 hover:bg-orange-400/10 transition-colors"
                  title="Visual/Editar PPT"
                >
                  <Presentation size={12} />
                </button>
              )}
              {canEdit && (
              <button
                type="button"
                onClick={() => onRemoveItem(item.id)}
                className="p-1 rounded text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
                title="Remover Item"
              >
                <Trash2 size={12} />
              </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
